/**
 * Set your password (2026-10-06): the page a set-password link opens (/set-password?token=…),
 * public and in the sign-in page's look. It checks the link first: not valid, used or expired says
 * so and points to the administrator; a good link shows whose account it is and asks for the new
 * password twice. Done, it offers the sign-in. The API (backend-node lib/password-link.js) signs the
 * user out everywhere and spends the link.
 */
import { useEffect, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { ApiError } from '@/api/client'
import distribuaiMark from '@/assets/figma/brand/distribuai-mark.svg'
import { Button, TextInput } from '@/kit'
import { ArcHalo, Oil } from '../LoginPage'
import { TenantMark } from '../shell/TenantMark'
import type { TenantSettings } from '../tenant'
import './i18n'
import { fetchPasswordLink, setPasswordWithLink } from './passwordApi'

const CARD = 'flex w-full flex-col gap-[24px] rounded-[16px] bg-[var(--surface-raised)] p-[40px] shadow-[0_1px_2px_0_rgb(16_16_19/0.06),0_12px_40px_-8px_rgb(16_16_19/0.16)] max-sm:p-[24px]'
const MIN_LENGTH = 12

function codeOf(error: unknown): string {
    return error instanceof ApiError && error.code ? error.code : 'failed'
}

export function SetPasswordPage({ tenant, logoHeight, demoLabel, demoHint }: { tenant: TenantSettings['data']['tenant']; logoHeight?: number | null; demoLabel?: string | null; demoHint?: string | null }) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const [password, setPassword] = useState('')
    const [repeat, setRepeat] = useState('')
    const [mismatch, setMismatch] = useState(false)

    useEffect(() => {
        document.title = `${t('setpw.title')} · ${tenant.name}`
    }, [t, tenant.name])

    const link = useQuery({
        queryKey: ['password-link', token],
        queryFn: ({ signal }) => fetchPasswordLink(token, signal),
        enabled: token !== '',
        retry: false,
        staleTime: Infinity,
    })
    const minLength = link.data?.min_length ?? MIN_LENGTH
    const save = useMutation({ mutationFn: () => setPasswordWithLink(token, password) })

    function onSubmit(event: FormEvent) {
        event.preventDefault()
        if (password !== repeat) {
            setMismatch(true)
            return
        }
        setMismatch(false)
        save.mutate()
    }

    const toSignIn = () => navigate('/login', { replace: true })
    const failure = token === '' ? 'link_invalid' : link.isError ? codeOf(link.error) : null

    let card: ReactNode
    if (save.isSuccess) {
        card = (
            <section aria-labelledby="setpw-title" className={CARD}>
                <div className="flex flex-col gap-[6px]">
                    <h1 id="setpw-title" className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.3px] text-[var(--text-default)]">
                        {t('setpw.doneTitle')}
                    </h1>
                    <p role="status" className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">
                        {t('setpw.done', { email: link.data?.email ?? '' })}
                    </p>
                </div>
                <Button variant="primary" className="min-h-[40px] w-full" onClick={toSignIn}>
                    {t('setpw.signIn')}
                </Button>
            </section>
        )
    } else if (failure) {
        card = (
            <section aria-labelledby="setpw-title" className={CARD}>
                <div className="flex flex-col gap-[6px]">
                    <h1 id="setpw-title" className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.3px] text-[var(--text-default)]">
                        {t('setpw.title')}
                    </h1>
                    <p role="alert" data-testid="setpw-link-problem" className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">
                        {t(`setpw.error.${failure}`, { defaultValue: t('setpw.error.link_invalid') })}
                    </p>
                </div>
                <Button className="min-h-[40px] w-full" onClick={toSignIn}>
                    {t('setpw.signIn')}
                </Button>
            </section>
        )
    } else if (link.isPending) {
        card = (
            <section className={CARD} aria-busy>
                <p role="status" className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">
                    {t('setpw.checking')}
                </p>
            </section>
        )
    } else {
        const saveCode = save.isError ? codeOf(save.error) : null
        card = (
            <form onSubmit={onSubmit} aria-labelledby="setpw-title" className={CARD}>
                <div className="flex flex-col gap-[6px]">
                    <h1 id="setpw-title" className="m-0 text-[22px] leading-[28px] font-semibold tracking-[-0.3px] text-[var(--text-default)]">
                        {t('setpw.title')}
                    </h1>
                    <p className="m-0 text-[14px] leading-[20px] [overflow-wrap:anywhere] text-[var(--text-weak)]">{t('setpw.for', { email: link.data?.email ?? '' })}</p>
                </div>
                {/* For password managers: the account the new password belongs to. */}
                <input type="email" name="username" autoComplete="username" value={link.data?.email ?? ''} readOnly hidden />
                <div className="flex flex-col gap-[20px]">
                    <TextInput label={t('setpw.new')} type="password" name="new-password" autoComplete="new-password" required minLength={minLength} value={password} onChange={(e) => setPassword(e.target.value)} />
                    <TextInput
                        label={t('setpw.repeat')}
                        type="password"
                        name="repeat-password"
                        autoComplete="new-password"
                        required
                        value={repeat}
                        onChange={(e) => setRepeat(e.target.value)}
                        error={mismatch ? t('setpw.mismatch') : undefined}
                    />
                    <p className="m-0 text-[13px] leading-[18px] text-[var(--text-weak)]">{t('setpw.hint', { count: minLength })}</p>
                    {saveCode && (
                        <p role="alert" className="m-0 text-[14px] leading-[20px] text-[var(--text-danger)]">
                            {t(`setpw.error.${saveCode}`, { count: minLength, defaultValue: t('setpw.error.failed') })}
                        </p>
                    )}
                </div>
                <Button variant="primary" type="submit" disabled={save.isPending} className="min-h-[40px] w-full">
                    {save.isPending ? t('setpw.saving') : t('setpw.submit')}
                </Button>
            </form>
        )
    }

    return (
        <div className="relative flex min-h-screen flex-col items-center justify-center overflow-clip bg-[var(--surface-page)] px-[16px] py-[48px] max-sm:py-[24px]">
            <ArcHalo />
            <Oil className="top-1/2 left-1/2 h-[640px] w-[880px] -translate-x-1/2 -translate-y-1/2 rounded-[50%] opacity-[0.55] blur-[100px] max-sm:h-[460px] max-sm:w-[460px]" />
            <div className="relative flex w-full max-w-[400px] flex-col items-center gap-[24px]">
                <TenantMark logoUrl={tenant.logoUrl} name={tenant.name} logoHeight={Math.max(logoHeight ?? 20, 24)} demoLabel={demoLabel} demoHint={demoHint} />
                {card}
                <img src={distribuaiMark} alt="distribuAI" width={71.538} height={20.103} className="mt-[8px] block h-auto w-[80px] opacity-80" />
            </div>
        </div>
    )
}
